import React from "react";

import {
  BarChartOutlined,
  ExperimentOutlined,
  BulbOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Image, Typography } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { LOGO_ASSETS } from "@falcon-terminal/shared-ui/constants/logos";

import { AppShellLayout } from "@/components";
import { ImpersonationBanner, useImpersonation } from "@/components/ImpersonationBanner";
import { OrgSelector } from "@/features/terminal/components/OrgSelector";

interface MainLayoutProps {
  children?: React.ReactNode;
}

const getCurrentView = (pathname: string) => {
  if (pathname.startsWith("/strategy")) {
    return "strategy-studio";
  }

  if (pathname.startsWith("/innovation")) {
    return "usecase-landing";
  }

  if (pathname.startsWith("/analysis")) {
    return "insights-command-center";
  }

  return "terminal";
};

const getPageTitle = (view: string) => {
  switch (view) {
    case "strategy-studio":
      return "Strategy Studio";
    case "usecase-landing":
      return "Innovation Studio";
    case "insights-command-center":
      return "Insights Command Center";
    default:
      return "Falcon Terminal";
  }
};

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getContext, isImpersonating } = useImpersonation();
  const currentView = getCurrentView(pathname);
  const pageTitle = getPageTitle(currentView);
  const impersonationContext = getContext();
  const showOrgSettings =
    !isImpersonating() ||
    (impersonationContext && impersonationContext.orgRole !== "Member");

  const handleNavigate = (
    view:
      | "terminal"
      | "strategy-studio"
      | "usecase-landing"
      | "insights-command-center",
  ) => {
    switch (view) {
      case "terminal":
        navigate("/terminal");
        break;
      case "strategy-studio":
        navigate("/strategy");
        break;
      case "usecase-landing":
        navigate("/innovation");
        break;
      case "insights-command-center":
        navigate("/analysis");
        break;
      default:
        break;
    }
  };

  const navigationItems = [
    {
      key: "strategy-studio",
      label: "Strategy Studio",
      description: "Strategic planning & roadmaps",
      icon: <BulbOutlined />,
      onClick: () => handleNavigate("strategy-studio"),
      title: "Strategy Studio",
    },
    {
      key: "usecase-landing",
      label: "Innovation Studio",
      description: "Experiment design & testing",
      icon: <ExperimentOutlined />,
      onClick: () => handleNavigate("usecase-landing"),
      title: "Innovation Studio",
    },
    {
      key: "insights-command-center",
      label: "Insights Command Center",
      description: "Analytics & decision hub",
      icon: <BarChartOutlined />,
      onClick: () => handleNavigate("insights-command-center"),
      title: "Insights Command Center",
    },
  ];

  return (
    <AppShellLayout
      banner={<ImpersonationBanner />}
      brandIcon={
        <Image
          preview={false}
          src={LOGO_ASSETS.logomark.svg}
          alt={LOGO_ASSETS.logomark.alt}
          height={20}
        />
      }
      brandName="FalconFirst"
      navigationItems={navigationItems}
      selectedNavigationKey={currentView}
      headerTitle={pageTitle}
      sidebarTopContent={({ expanded }) => (
        <OrgSelector isExpanded={expanded} />
      )}
      sidebarFooter={({ expanded }) =>
        showOrgSettings ? (
          <Button
            type="text"
            block
            icon={<SettingOutlined style={{ color: "#0A78C9" }} />}
            style={{
              height: expanded ? 48 : 40,
              color: "rgba(255, 255, 255, 0.85)",
              justifyContent: expanded ? "flex-start" : "center",
              borderRadius: 10,
              border: expanded
                ? "1px solid transparent"
                : "1px solid rgba(255, 255, 255, 0.1)",
              background: expanded
                ? "transparent"
                : "rgba(255, 255, 255, 0.02)",
            }}
            onClick={() => undefined}
          >
            {expanded ? (
              <Typography.Text
                style={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                Org Settings
              </Typography.Text>
            ) : null}
          </Button>
        ) : null
      }
    >
      {children || <Outlet />}
    </AppShellLayout>
  );
};

export default MainLayout;
